/**
 * Exercise Theme Service
 * Manages CRUD operations for exercise themes stored in Firestore.
 * Themes are global (not per-user) — stored at top-level `exerciseThemes` collection.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  onSnapshot,
  Unsubscribe,
  FieldValue,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ExerciseThemeDoc } from '../types';

const EXERCISE_THEMES_COLLECTION = 'exerciseThemes';

/**
 * Get the exerciseThemes collection reference
 */
const getThemesCollectionRef = () => {
  return collection(db, EXERCISE_THEMES_COLLECTION);
};

/**
 * Get a specific theme document reference
 */
const getThemeDocRef = (themeId: string) => {
  return doc(db, EXERCISE_THEMES_COLLECTION, themeId);
};

/**
 * Fetch all exercise themes, ordered by `order` field
 */
export const getAllExerciseThemes = async (): Promise<ExerciseThemeDoc[]> => {
  const themesRef = getThemesCollectionRef();
  const q = query(themesRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ ...d.data(), id: d.id }) as ExerciseThemeDoc);
};

/**
 * Fetch a single theme by ID
 */
export const getExerciseTheme = async (themeId: string): Promise<ExerciseThemeDoc | null> => {
  const themeRef = getThemeDocRef(themeId);
  const snapshot = await getDoc(themeRef);
  if (!snapshot.exists()) return null;
  return { ...snapshot.data(), id: snapshot.id } as ExerciseThemeDoc;
};

/**
 * Create a new exercise theme (uses theme.id as doc ID).
 * Strips undefined optional fields — setDoc does not accept undefined values.
 */
export const createExerciseTheme = async (theme: ExerciseThemeDoc): Promise<void> => {
  const themeRef = getThemeDocRef(theme.id);
  const sanitized = Object.fromEntries(
    Object.entries(theme).filter(([, v]) => v !== undefined)
  ) as ExerciseThemeDoc;
  await setDoc(themeRef, sanitized);
};

/**
 * Partial update of an existing exercise theme.
 * Converts undefined values to deleteField() so Firestore removes those fields.
 */
export const updateExerciseTheme = async (
  themeId: string,
  updates: Partial<ExerciseThemeDoc>
): Promise<void> => {
  const themeRef = getThemeDocRef(themeId);
  const sanitized: Record<string, unknown | FieldValue> = {};
  for (const [key, value] of Object.entries(updates)) {
    sanitized[key] = value === undefined ? deleteField() : value;
  }
  await updateDoc(themeRef, sanitized);
};

/**
 * Delete an exercise theme
 */
export const deleteExerciseTheme = async (themeId: string): Promise<void> => {
  const themeRef = getThemeDocRef(themeId);
  await deleteDoc(themeRef);
};

/**
 * Subscribe to all exercise themes with real-time updates, ordered by `order`
 */
export const subscribeToExerciseThemes = (
  callback: (themes: ExerciseThemeDoc[]) => void
): Unsubscribe => {
  const themesRef = getThemesCollectionRef();
  const q = query(themesRef, orderBy('order', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const themes = snapshot.docs.map(d => ({ ...d.data(), id: d.id }) as ExerciseThemeDoc);
      callback(themes);
    },
    (error) => {
      console.error('Error subscribing to exercise themes:', error);
      callback([]);
    }
  );
};
